const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: Record<string, unknown> | null;
}

interface ApiError {
  message: string;
  [key: string]: unknown;
}

async function apiRequest<T = unknown>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Merge custom headers if provided
  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  // @ts-expect-error - body is optional
  const config: RequestInit = {
    ...options,
    credentials: 'include', // Important: sends cookies with requests
    headers,
  };

  // Convert body to JSON if it's an object
  if (options.body !== null && options.body !== undefined) {
    config.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, config);

  // Handle auth errors (but don't redirect if already on public pages)
  if (response.status === 401) {
    const currentPath = window.location.pathname;
    const publicPaths = ['/', '/landing', '/login', '/register', '/forgot-password', '/reset-password'];
    if (!publicPaths.includes(currentPath)) {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  // Handle other errors
  if (!response.ok) {
    let errorMessage = 'Request failed';
    try {
      const errorData: ApiError = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // If parsing fails, use default message
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  // Return JSON response for successful requests
  return response.json();
}

export const api = {
  get: <T = unknown>(endpoint: string) => apiRequest<T>(endpoint, { method: 'GET' }),
  post: <T = unknown>(endpoint: string, body?: Record<string, unknown>) =>
    apiRequest<T>(endpoint, { method: 'POST', body }),
  put: <T = unknown>(endpoint: string, body?: Record<string, unknown>) =>
    apiRequest<T>(endpoint, { method: 'PUT', body }),
  delete: <T = unknown>(endpoint: string) => apiRequest<T>(endpoint, { method: 'DELETE' }),
} as const;
